import type { TaskType } from 'api-validations/ag-concepts'
import type { EventCode } from 'api-validations/stitch'

import type { DetailsTab, MapMode } from '../core'
import type {
  AsAppliedLayer,
  EditorLayer,
  LayerSelection,
  StreamGroup,
  StreamPartition
} from '../data-layer-state'
import type { AssetIdentity } from '../data-layer-state/core'
import { type BrowseFields, type FilterState, type InField, Pane, type PaneScope } from '../session'
import type { MapAddress } from './address'
import type { Cff } from './catalog'
import { EquivalencyOutcome } from './layer-scope'

export { Pane }

/**
 * A gesture that cannot be committed by swapping its parameters into place: it
 * either leaves something for the application to decide, or discards state that
 * was scoped to what it replaced. Gestures that do neither — the browse list's
 * season filter and sort, legend overrides, note hover — write their own slice
 * and are absent here.
 *
 * Each variant is named for what the user saw themselves choose, not for what
 * the choice resolves to. A season dropdown emits a crop cycle and a field list
 * emits a field; the stream underneath is a conclusion, and every control that
 * reaches one on its own reaches a slightly different one.
 *
 * `Enter*` marks the variants that cross a scope boundary — into a field, into
 * split, into an editor.
 */
export type RequestedMapAction =
  // Entering the map. No pane: there is only one until split is entered.
  /** Picked a field by name; which of its passes to show is not part of the ask. */
  | { kind: 'EnterField'; cff: Cff }
  /** Picked one pass from the browse timeline. Application and layer still open. */
  | { kind: 'EnterStream'; streamId: string }
  /**
   * Arrived at an address, from a link inside the app or outside it. Resolved
   * without reference to prior state: an address is total, so anything it does
   * not carry lands on its default rather than being inherited. A cold load and
   * an in-app navigation therefore reach the same state from the same link.
   */
  | { kind: 'NavigateToAddress'; address: MapAddress }

  /**
   * Both panes are always in one field, so this moves the page rather than a
   * pane — and each pane resolves its own landing place, since what a pane was
   * showing is the only guide to what it should show next.
   */
  | { kind: 'ChangeField'; cff: Cff }

  // Moving a pane.
  | { kind: 'SelectCropCycle'; pane: Pane; year: number; cropKey: string }
  | { kind: 'SelectTask'; pane: Pane; task: TaskType }
  | { kind: 'SelectStreamGroup'; pane: Pane; group: StreamGroup }
  // Editing a group's membership, which can move a pane the user was not aiming
  // at: the group it sits in may come out with a different primary.
  | {
      kind: 'AddStreamToGroup'
      pane: Pane
      streamId: string
      /** Any member of the destination group — the picker names its primary. */
      groupMember: string
    }
  /** Takes a stream out into a group of its own; a sole member has nowhere to go. */
  | { kind: 'RemoveStreamFromGroup'; pane: Pane; streamId: string }
  /**
   * The escape hatch for pickers that already name a concrete target. Listed
   * because a target in another stream still discards that stream's state.
   */
  | { kind: 'SelectLayer'; pane: Pane; layer: LayerSelection }

  // The scrubber. Two ways to arrive at one record, differing only in whether
  // the position left behind becomes somewhere the user can return to.
  /** A record the user picked: map click, scrubber drag, diagnostics-table jump. */
  | { kind: 'SetRecordWithHistory'; record: number }
  /** A record the transport controls moved to: next, previous, first, last. */
  | { kind: 'SetRecordWithoutHistory'; record: number }
  | { kind: 'GoBackRecord' }
  | { kind: 'GoForwardRecord' }
  /**
   * A click on the pass asking for the record driven nearest it. Listed
   * because the point becomes a record only through a lookup.
   */
  | { kind: 'PickRecordAtPoint'; coordinate: [number, number] }

  /**
   * Jumped from an event-code marker to the code's diagnose table. Which of the
   * code's modules to show, and the layer that application can render, are both
   * left to the resolution; the record travels with the code.
   */
  | { kind: 'ViewDiagnoseTable'; code: EventCode }

  // Editors. Main-pane only, so no pane to name.
  | { kind: 'EnterPlanEditor'; planningFieldId: string; planId: string | null }
  | {
      kind: 'EnterPlanAssetEditor'
      planningFieldId: string
      planId: string | null
      asset: AssetIdentity
    }
  | { kind: 'EnterBoundaryCreator'; planningFieldId: string }
  | { kind: 'EnterPrescriptionCreator'; planningFieldId: string }
  /** Where to land is the captured return target, or a decision when there is none. */
  | { kind: 'ExitEditor' }

  // Split. What fills the new pane, and what becomes of the old one's layer,
  // are both left to the application.
  | { kind: 'EnterSplit' }
  | { kind: 'ExitSplit' }

  // Page.
  | { kind: 'SetMode'; mode: MapMode }
  | { kind: 'SetDetailsTab'; tab: DetailsTab }
  /**
   * Narrowed the field list to a client or a farm. Listed because the same
   * picker is offered in-field, where a filter the current field falls outside
   * of cannot be honored without leaving it — the field list is the only place
   * the filter means anything.
   */
  | { kind: 'SetBrowseFilter'; filter: FilterState }
  | { kind: 'ReturnToBrowseFields' }

/**
 * The two ways a request can leave the map where it was, as one thing to render.
 * They are one union rather than two nullable fields because a caller that
 * handles either must know about the other, and two fields let it ship having
 * handled only one.
 *
 * They are still two cases because they call for different UI: a lookup that
 * failed is worth retrying and has nothing specific to say, while a request that
 * could not be honored says exactly what was missing and will say it again.
 */
export type MapActionFailure =
  | { kind: 'Unresolvable'; reason: UnresolvableReason }
  | { kind: 'LookupFailed'; error: Error }

/**
 * Everything a pane holds after a move. The scope travels with the layer
 * because only a resolution can work it out — the reducer sees the pass being
 * left behind but not the crop cycle it belonged to.
 */
export type PaneTarget = {
  layer: LayerSelection
  scope: PaneScope | null
}

/**
 * A `RequestedMapAction` with every lookup already done — the payload the
 * reducer commits. Nothing here needs the catalog, so the reducer stays pure
 * and every producer commits under the same rules.
 */
export type ResolvedMapAction =
  | { kind: 'SetPaneLayer'; pane: Pane; layer: LayerSelection; scope: PaneScope | null }
  | { kind: 'SetBothPanes'; main: PaneTarget; comparison: PaneTarget }
  /** Moves a pane's pickers without moving what it draws. */
  | { kind: 'SetPaneScope'; pane: Pane; scope: PaneScope }
  /**
   * A pane's regrouped streams, and where the pane lands as a result. The two
   * commit together because the second follows from the first: a group's primary
   * is its latest member, so a pane sitting in that group moves when the
   * membership does. `target` is null when no pane sat in the affected scope.
   */
  | {
      kind: 'SetStreamGrouping'
      pane: Pane
      scopeKey: string
      partition: StreamPartition
      target: PaneTarget | null
    }
  | {
      kind: 'EnterEditor'
      layer: EditorLayer
      scope: PaneScope | null
    }
  /**
   * Reconciliation, not a user action: the comparison pane's stream is absent
   * from the operation's stream list, so the pane cannot render. Reachable when
   * that list is refetched mid-session.
   */
  | { kind: 'DropStaleComparison' }
  | { kind: 'SetComparisonDisabled'; disabled: boolean }
  | { kind: 'SetMode'; mode: MapMode }
  | { kind: 'SetDetailsTab'; tab: DetailsTab }
  /** Narrows a list that is already on screen, so nothing else moves. */
  | { kind: 'SetBrowseFilter'; filter: FilterState }
  /**
   * The one way into the field list, whether the user asked for it or a filter
   * left no field to stay in. The session is carried rather than defaulted so
   * the list can arrive already narrowed.
   */
  | { kind: 'ReturnToBrowseFields'; session: BrowseFields }
  // The scrubber's four requests reach the reducer unchanged: the record and
  // the jump stacks are all in prior state, so there is nothing to look up.
  | { kind: 'SetRecordWithHistory'; record: number }
  | { kind: 'SetRecordWithoutHistory'; record: number }
  | { kind: 'GoBackRecord' }
  | { kind: 'GoForwardRecord' }
  | {
      kind: 'ViewDiagnoseTable'
      layer: AsAppliedLayer
      scope: PaneScope | null
      record: number
      focusedModules: readonly string[]
    }
  | {
      kind: 'RestoreSession'
      /**
       * In-field only: an address that names no field resolves to the field list
       * instead, so that one landing keeps its single owner above.
       */
      session: InField
      record: number
      mode: MapMode
      activeNoteId: string | null
    }

export enum UnresolvableReason {
  FieldHasNoStreams = 'FieldHasNoStreams',
  NoStreamInCropCycle = 'NoStreamInCropCycle',
  NoStreamForTask = 'NoStreamForTask',
  FieldNotRegisteredForPlanning = 'FieldNotRegisteredForPlanning',
  ScopeHasNoLayers = 'ScopeHasNoLayers',
  /** A named stream the operation does not have — a dead link, or a deleted pass. */
  StreamNotFound = 'StreamNotFound'
}

/**
 * The outcome of resolving one action. `Unresolvable` is a value rather than a
 * logged no-op so the UI can explain why a click did nothing.
 *
 * `equivalency` grades how well the commit honored the key the user was on, so
 * a caller can tell them the metric moved to another system — or that it is
 * gone entirely — without re-deriving it from the layer that came back. It
 * grades the layer match alone: a resolution can also depart from the request
 * at the stream level, falling back to another pass when the named one is gone,
 * which a single value cannot express alongside the layer's outcome.
 */
export type Resolution =
  | { kind: 'Commit'; action: ResolvedMapAction; equivalency: EquivalencyOutcome }
  | { kind: 'NoOp' }
  | { kind: 'Unresolvable'; reason: UnresolvableReason }
